/**
 * Wallboard trend by week / month / quarter / year — bars from the stored
 * period records (`GET /orders/v1/periods`, "due in period"), drawn exactly
 * like the daily bars (same metrics, colours and merge), one group per period.
 *
 * A period with no stored figures is a gap, never a zero bar. A partial
 * period (some days without stored figures) is marked `*` and explained in
 * the coverage note, as is a closed period that was restated.
 */

import { addUtcDays } from '../lib/calc';
import type { OrdersPeriod, OrdersPeriodGrain, OrdersPeriodsResponse } from '../types/ordersDailyTruth';
import {
  mergeDailyRateSeriesForBarChart,
  type ChartMetricKind,
  type CompletionRateBarGroup,
  type DailyRatePoint,
} from './ordersDailyRates';

export type TrendGrain = 'day' | OrdersPeriodGrain;

export const TREND_GRAIN_OPTIONS: readonly { id: TrendGrain; label: string }[] = [
  { id: 'day', label: 'Days' },
  { id: 'week', label: 'Weeks' },
  { id: 'month', label: 'Months' },
  { id: 'quarter', label: 'Quarters' },
  { id: 'year', label: 'Years' },
];

/** How many periods the trend shows per grain (ending with the current one). */
export const PERIOD_TREND_COUNT: Record<OrdersPeriodGrain, number> = { week: 12, month: 12, quarter: 8, year: 3 };

const pad2 = (n: number) => String(n).padStart(2, '0');

/** First day of the period of `grain` containing `date` (ISO weeks start Monday). */
export function periodStart(grain: OrdersPeriodGrain, date: string): string {
  const d = new Date(`${date}T00:00:00.000Z`);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth() + 1;
  switch (grain) {
    case 'week':
      return addUtcDays(date, -((d.getUTCDay() + 6) % 7));
    case 'month':
      return `${y}-${pad2(m)}-01`;
    case 'quarter':
      return `${y}-${pad2(Math.floor((m - 1) / 3) * 3 + 1)}-01`;
    case 'year':
      return `${y}-01-01`;
  }
}

function addMonths(isoFirstOfMonth: string, months: number): string {
  const d = new Date(`${isoFirstOfMonth}T00:00:00.000Z`);
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString().slice(0, 10);
}

/** The date range covering the last `count` periods up to the one containing `endDate`. */
export function periodTrendRange(grain: OrdersPeriodGrain, endDate: string, count = PERIOD_TREND_COUNT[grain]): { from: string; to: string } {
  const start = periodStart(grain, endDate);
  const back = count - 1;
  const from = grain === 'week'
    ? addUtcDays(start, -7 * back)
    : addMonths(start, -back * (grain === 'month' ? 1 : grain === 'quarter' ? 3 : 12));
  return { from, to: endDate };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Short name, e.g. `W41`, `Oct 2026`, `Q4 2026`, `2026`. */
export function periodShortName(grain: OrdersPeriodGrain, period: Pick<OrdersPeriod, 'key' | 'start_date'>): string {
  const year = period.start_date.slice(0, 4);
  switch (grain) {
    case 'week':
      return period.key.slice(5);
    case 'month':
      return `${MONTHS[Number(period.start_date.slice(5, 7)) - 1]} ${year}`;
    case 'quarter':
      return `${period.key.slice(5)} ${year}`;
    case 'year':
      return year;
  }
}

/** Two-line x-axis label (BarChart splits on `\n`); `*` marks a partial period. */
function periodLabel(grain: OrdersPeriodGrain, period: OrdersPeriod): string {
  const star = period.partial ? ' *' : '';
  const ddmm = `${period.start_date.slice(8, 10)}/${period.start_date.slice(5, 7)}`;
  switch (grain) {
    case 'week':
      return `${period.key.slice(5)}${star}\n(${ddmm})`;
    case 'month':
      return `${MONTHS[Number(period.start_date.slice(5, 7)) - 1]}${star}\n${period.start_date.slice(0, 4)}`;
    case 'quarter':
      return `${period.key.slice(5)}${star}\n${period.start_date.slice(0, 4)}`;
    case 'year':
      return `${period.key}${star}`;
  }
}

function point(grain: OrdersPeriodGrain, period: OrdersPeriod, value: number | null, consignments: number, completed: number): DailyRatePoint | null {
  if (value == null) return null;
  return { date: period.start_date, label: periodLabel(grain, period), value, consignments, completed };
}

/**
 * Bar groups for the period trend, oldest first. The period containing
 * `todayDate` gets the "today" (current) tone, as today's bar does by day.
 */
export function buildTrendBarGroupsFromPeriods(
  grain: OrdersPeriodGrain,
  periods: readonly OrdersPeriod[],
  metric: ChartMetricKind,
  todayDate: string,
): CompletionRateBarGroup[] {
  const series = (dir: 'deliveries' | 'collections') => periods.flatMap((p) => {
    const progress = p.figures[dir];
    if (progress.total.consignments === 0) return [];
    const value = metric === 'completion_rate'
      ? progress.completion_pct
      : metric === 'consignments'
        ? progress.completed.consignments
        : progress.completed.lifts;
    const pt = point(grain, p, value, progress.total.consignments, progress.completed.consignments);
    return pt ? [pt] : [];
  });
  const eta = metric === 'completion_rate'
    ? periods.flatMap((p) => {
      const e = p.figures.eta;
      if (e.total.consignments === 0) return [];
      const pt = point(grain, p, e.completion_pct, e.total.consignments, e.on_time.consignments);
      return pt ? [pt] : [];
    })
    : undefined;
  return mergeDailyRateSeriesForBarChart(series('deliveries'), series('collections'), {
    todayDate: periodStart(grain, todayDate),
    eta,
  });
}

/** The consignment counts a reader looks for first, in order. */
const HEADLINE_CHANGES: ReadonlyArray<[string, string]> = [
  ['deliveries.completed.consignments', 'deliveries completed'],
  ['collections.completed.consignments', 'collections completed'],
  ['deliveries.total.consignments', 'deliveries due'],
  ['collections.total.consignments', 'collections due'],
  ['deliveries.moved_out.consignments', 'deliveries re-booked out'],
];

/** e.g. "12 Oct: deliveries completed 210 → 214"; null when there is nothing headline-worthy. */
export function restatementDetail(period: Pick<OrdersPeriod, 'last_restatement'>): string | null {
  const r = period.last_restatement;
  if (!r) return null;
  const byPath = new Map(r.changes.map((c) => [c.path, c]));
  const shown = HEADLINE_CHANGES
    .map(([path, label]) => [byPath.get(path), label] as const)
    .filter(([c]) => c !== undefined)
    .slice(0, 2)
    .map(([c, label]) => `${label} ${c!.previous ?? '—'} → ${c!.new ?? '—'}`);
  if (shown.length === 0) return null;
  const day = new Date(r.computed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'Europe/London' });
  return `${day}: ${shown.join(', ')}`;
}

/** What the bars leave out or qualify, in one line; null when nothing. */
export function periodTrendCoverageNote(
  grain: OrdersPeriodGrain,
  periods: readonly OrdersPeriod[],
  unavailable: OrdersPeriodsResponse['unavailable'],
  stale: boolean,
): string | null {
  const parts: string[] = [];
  const partial = periods.filter((p) => p.partial);
  if (partial.length > 0) {
    parts.push(`* Partial: ${partial.map((p) => `${periodShortName(grain, p)} (${p.coverage.covered} of ${p.coverage.days} days)`).join(', ')} — figures cover only days with stored data.`);
  }
  const noFacts = unavailable.filter((u) => u.reason === 'no_facts');
  if (noFacts.length > 0) {
    parts.push(`No stored figures for ${noFacts.length} earlier ${noFacts.length === 1 ? 'period' : 'periods'} (shown as gaps, not zero).`);
  }
  if (unavailable.some((u) => u.reason === 'not_computed')) {
    parts.push('Some periods are still being prepared — they appear within a few minutes.');
  }
  const restated = periods.filter((p) => p.restated);
  if (restated.length > 0) {
    parts.push(`Restated after closing: ${restated.map((p) => {
      const detail = restatementDetail(p);
      return detail ? `${periodShortName(grain, p)} (${detail})` : periodShortName(grain, p);
    }).join(', ')}.`);
  }
  if (stale) parts.push('Figures are from an earlier build; an update has been requested.');
  return parts.length > 0 ? parts.join(' ') : null;
}
